import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Check, Crown, RefreshCw, XCircle, CalendarClock, CreditCard } from "lucide-react";
import FoundingCard from "@/components/FoundingCard";

const PLANS = [
  { id: "free", name: "Free", monthly: 0, annual: 0, period: "forever", features: ["Readiness Score", "5 opportunity matches/mo", "Basic profile"], purchasable: false },
  { id: "growth", name: "Growth", monthly: 50, annual: 500, features: ["Unlimited opportunities", "AI Qualification & Win Scores", "Growth Roadmap"], purchasable: true, highlight: true },
  { id: "pro", name: "Pro", monthly: 100, annual: 1000, features: ["AI Bid Triage", "Competitive Intelligence", "Bid CRM pipeline"], purchasable: true },
  { id: "enterprise", name: "Enterprise", monthly: 999, annual: 9990, features: ["Team seats & roles", "Dedicated manager", "API access"], purchasable: true },
];

const fmtDate = (iso) => { try { return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }); } catch { return iso; } };

export default function Billing() {
  const { user, refresh } = useAuth();
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(null);
  const [polling, setPolling] = useState(false);
  const [sub, setSub] = useState(null);
  const [acting, setActing] = useState(false);
  const [interval, setInterval] = useState("monthly");

  const loadSub = useCallback(async () => {
    try { const r = await api.get("/subscription"); setSub(r.data); } catch (e) {}
  }, []);
  useEffect(() => { loadSub(); }, [loadSub, user]);

  const pollStatus = useCallback(async (sessionId, attempts = 0) => {
    if (attempts >= 6) { setPolling(false); toast.error("Payment status check timed out."); return; }
    try {
      const r = await api.get(`/payments/status/${sessionId}`);
      if (r.data.status === "complete") {
        setPolling(false);
        toast.success(r.data.payment_status === "no_payment_required" ? "Your free trial is active!" : "Payment successful — subscription active!");
        await refresh(); await loadSub();
        navigate("/dashboard/billing", { replace: true });
        return;
      }
      if (r.data.status === "expired") { setPolling(false); toast.error("Payment session expired."); return; }
      setTimeout(() => pollStatus(sessionId, attempts + 1), 2000);
    } catch (e) { setPolling(false); toast.error("Could not verify payment."); }
  }, [refresh, loadSub, navigate]);

  useEffect(() => {
    const sessionId = params.get("session_id");
    if (sessionId) {
      setPolling(true); pollStatus(sessionId);
      params.delete("session_id"); setParams(params, { replace: true });
    }
    if (params.get("crm_add") === "upgrade") {
      toast.info("Upgrade to Pro to track that bid in your CRM pipeline.");
      params.delete("crm_add"); setParams(params, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkout = async (plan) => {
    setBusy(plan);
    try {
      const r = await api.post("/payments/checkout", { plan, interval, origin_url: window.location.origin });
      window.location.href = r.data.url;
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not start checkout — please try again."); setBusy(null); }
  };

  const openPortal = async () => {
    setActing(true);
    try {
      const r = await api.post("/payments/portal", { origin_url: window.location.origin });
      window.location.href = r.data.url;
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not open billing portal"); setActing(false); }
  };

  const cancel = async () => {
    setActing(true);
    try { await api.post("/payments/cancel"); await loadSub(); toast.success("Subscription will not renew after the current period."); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not cancel"); }
    finally { setActing(false); }
  };
  const confirmCancel = () => {
    const msg = isTrialing
      ? "Cancel your free trial? You won't be charged, and access continues until your trial ends."
      : "Cancel your subscription? You'll keep access until the end of the current period, then it won't renew.";
    if (window.confirm(msg)) cancel();
  };
  const reactivate = async () => {
    setActing(true);
    try { await api.post("/payments/reactivate"); await loadSub(); toast.success("Subscription reactivated."); }
    catch (e) { toast.error("Could not reactivate"); }
    finally { setActing(false); }
  };

  const isActive = sub?.subscription_status === "active";
  const isTrialing = sub?.is_trialing;
  const trialEligible = (sub?.trial_status ?? user?.trial_status) === "pending";
  const targetPlan = params.get("plan");

  useEffect(() => {
    if (!targetPlan) return;
    const el = document.querySelector(`[data-testid="billing-plan-${targetPlan}"]`);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [targetPlan, sub]);

  return (
    <div className="space-y-6" data-testid="billing-page">
      <div>
        <h1 className="font-heading text-3xl font-bold tracking-tight">Billing & Plans</h1>
        <p className="text-slate-500 mt-1">You're currently on the <span className="font-semibold text-slate-900 capitalize">{user?.plan}</span> plan.</p>
      </div>

      <div className="bg-emerald-50 border border-emerald-100 rounded-xl px-5 py-3.5" data-testid="billing-roi-note">
        <p className="text-sm text-emerald-900 font-medium">One won government contract pays for <span className="font-bold">years</span> of BidMatchAI.</p>
      </div>

      {polling && (
        <div className="bg-accent/10 border border-accent/20 rounded-xl p-4 text-sm text-slate-700 flex items-center gap-3" data-testid="payment-polling">
          <div className="h-4 w-4 border-2 border-accent/30 border-t-accent rounded-full animate-spin" /> Verifying your payment…
        </div>
      )}

      {/* Card + paid-start confirmation (during trial or as a "card on file" reassurance for active subs) */}
      {sub?.has_payment_method && isActive && (
        <div className="bg-white border border-emerald-200 rounded-2xl p-5 flex flex-col sm:flex-row sm:items-center gap-4" data-testid="card-confirmation">
          <div className="h-10 w-10 rounded-full bg-emerald-50 flex items-center justify-center shrink-0">
            <Check className="h-5 w-5 text-emerald-600" strokeWidth={3} />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-heading font-semibold text-slate-900 inline-flex items-center gap-2 flex-wrap">
              Card on file
              {sub.card_brand && sub.card_last4 && (
                <span className="text-xs font-medium text-slate-500 inline-flex items-center gap-1.5" data-testid="card-summary">
                  <CreditCard className="h-3.5 w-3.5" />
                  <span className="capitalize">{sub.card_brand}</span> ending in <span className="font-data">{sub.card_last4}</span>
                  {sub.card_exp_month && sub.card_exp_year && (
                    <span className="text-slate-400">· exp {String(sub.card_exp_month).padStart(2, "0")}/{String(sub.card_exp_year).slice(-2)}</span>
                  )}
                </span>
              )}
            </p>
            <p className="text-sm text-slate-500 mt-0.5" data-testid="paid-start-line">
              {isTrialing
                ? (sub.cancel_at_period_end
                    ? <>Trial ends <span className="font-semibold text-slate-800">{fmtDate(sub.current_period_end)}</span> — won&apos;t convert to paid because you&apos;ve canceled.</>
                    : <>Your paid subscription starts <span className="font-semibold text-emerald-800">{fmtDate(sub.paid_starts_at || sub.trial_end || sub.current_period_end)}</span>.</>)
                : (sub.cancel_at_period_end
                    ? <>You&apos;re on paid — access ends <span className="font-semibold text-slate-800">{fmtDate(sub.current_period_end)}</span>.</>
                    : <>You&apos;re on paid — next charge on <span className="font-semibold text-slate-800">{fmtDate(sub.current_period_end)}</span>.</>)}
            </p>
          </div>
          <button onClick={openPortal} disabled={acting} data-testid="update-card-button" className="shrink-0 text-sm font-medium rounded-md px-4 py-2 bg-white border border-slate-200 text-slate-700 hover:bg-slate-50 disabled:opacity-60 transition-colors inline-flex items-center gap-2">
            <CreditCard className="h-4 w-4" /> Update card
          </button>
        </div>
      )}

      {sub && sub.has_billing_account && !sub.has_payment_method && isActive && (
        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-5 flex flex-col sm:flex-row sm:items-center gap-4" data-testid="no-card-warning">
          <div className="h-10 w-10 rounded-full bg-amber-100 flex items-center justify-center shrink-0">
            <CreditCard className="h-5 w-5 text-amber-700" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-heading font-semibold text-amber-900">No card on file</p>
            <p className="text-sm text-amber-800 mt-0.5">
              {sub.trial_end || sub.current_period_end ? (
                <>Add a payment method before your trial ends on <span className="font-semibold">{fmtDate(sub.trial_end || sub.current_period_end)}</span> to keep your access uninterrupted.</>
              ) : (
                <>Add a payment method to keep your access uninterrupted.</>
              )}
            </p>
          </div>
          <button onClick={openPortal} disabled={acting} data-testid="add-card-button" className="shrink-0 text-sm font-medium rounded-md px-4 py-2 bg-emerald-900 text-white hover:bg-emerald-800 disabled:opacity-60 transition-colors inline-flex items-center gap-2">
            <CreditCard className="h-4 w-4" /> Add card
          </button>
        </div>
      )}

      {/* Subscription status */}
      {isActive && (
        <div className="bg-emerald-900 text-white rounded-2xl p-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-testid="subscription-status">
          <div className="flex items-start gap-3">
            <div className="h-10 w-10 rounded-lg bg-white/10 flex items-center justify-center shrink-0"><CalendarClock className="h-5 w-5" /></div>
            <div>
              <p className="font-heading font-semibold capitalize">{isTrialing ? `${sub.plan} — free trial active` : `${sub.plan} subscription active`}</p>
              <p className="text-sm text-slate-300">
                {isTrialing
                  ? (sub.cancel_at_period_end
                      ? <>Trial ends <span className="font-semibold text-white">{fmtDate(sub.current_period_end)}</span> — won't convert to paid.</>
                      : <>Free trial ends <span className="font-semibold text-white">{fmtDate(sub.current_period_end)}</span> — you'll be charged $50/mo then unless you cancel.</>)
                  : (sub.cancel_at_period_end
                      ? <>Ends on <span className="font-semibold text-white">{fmtDate(sub.current_period_end)}</span> — won't renew.</>
                      : <>Renews on <span className="font-semibold text-white">{fmtDate(sub.current_period_end)}</span>.</>)}
              </p>
            </div>
          </div>
          <div className="shrink-0 flex flex-col sm:flex-row gap-2">
            <button onClick={openPortal} disabled={acting} data-testid="manage-billing-button" className="bg-white text-slate-900 rounded-md px-4 py-2 text-sm font-medium hover:bg-slate-100 transition-colors inline-flex items-center gap-2">
              <CreditCard className="h-4 w-4" /> Manage billing
            </button>
            {sub.cancel_at_period_end ? (
              <button onClick={reactivate} disabled={acting} data-testid="reactivate-button" className="bg-white/10 text-white rounded-md px-4 py-2 text-sm font-medium hover:bg-white/20 transition-colors inline-flex items-center gap-2">
                <RefreshCw className="h-4 w-4" /> Resume subscription
              </button>
            ) : (
              <button onClick={cancel} disabled={acting} data-testid="cancel-subscription-button" className="bg-white/10 text-white rounded-md px-4 py-2 text-sm font-medium hover:bg-white/20 transition-colors inline-flex items-center gap-2">
                <XCircle className="h-4 w-4" /> Cancel subscription
              </button>
            )}
          </div>
        </div>
      )}

      <p className="text-xs text-slate-400">Subscriptions renew automatically. Manage your plan, payment method, or cancel anytime from the billing portal.</p>

      {/* Billing interval toggle */}
      <div className="flex items-center justify-center">
        <div className="inline-flex items-center gap-1 bg-slate-100 rounded-full p-1" data-testid="billing-interval-toggle">
          <button
            onClick={() => setInterval("monthly")}
            data-testid="interval-monthly"
            className={`rounded-full px-5 py-2 text-sm font-medium transition-colors ${interval === "monthly" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}
          >
            Monthly
          </button>
          <button
            onClick={() => setInterval("annual")}
            data-testid="interval-annual"
            className={`rounded-full px-5 py-2 text-sm font-medium transition-colors inline-flex items-center gap-2 ${interval === "annual" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}
          >
            Annual
            <span className="text-xs font-bold uppercase tracking-wide bg-emerald-100 text-emerald-700 rounded-full px-2 py-0.5">Save 17%</span>
          </button>
        </div>
      </div>

      <FoundingCard user={user} sub={sub} />

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
        {PLANS.filter((p) => p.id !== "free" || user?.plan === "free").map((p) => {
          const current = user?.plan === p.id;
          const isAnnual = interval === "annual" && p.purchasable;
          const priceDisplay = !p.purchasable ? "$0" : isAnnual ? `$${Math.round(p.annual / 12).toLocaleString()}` : `$${p.monthly.toLocaleString()}`;
          const periodDisplay = !p.purchasable ? "forever" : "/mo";
          const showTrial = trialEligible && !isActive && p.id === "growth";
          return (
            <div key={p.id} data-testid={`billing-plan-${p.id}`} className={`rounded-2xl border p-7 flex flex-col transition-all ${targetPlan === p.id ? "border-emerald-600 ring-2 ring-emerald-500/40 shadow-lg" : p.highlight ? "border-slate-900 shadow-md" : "border-slate-200"} bg-white`}>
              {p.highlight && <span className="self-start text-xs font-bold uppercase tracking-wider bg-emerald-900 text-white rounded-full px-3 py-1 mb-3 inline-flex items-center gap-1"><Crown className="h-3 w-3" /> Most Popular</span>}
              <h3 className="font-heading text-lg font-semibold">{p.name}</h3>
              <div className="mt-2 flex items-end gap-1">
                <span className="font-data text-3xl font-semibold" data-testid={`price-${p.id}`}>{priceDisplay}</span>
                <span className="mb-1 text-sm text-slate-500">{periodDisplay}</span>
              </div>
              <div className="h-5 mt-1">
                {showTrial
                  ? <p className="text-xs text-emerald-700 font-semibold" data-testid="growth-trial-note">5-day free trial, then ${p.monthly}/mo</p>
                  : isAnnual && <p className="text-xs text-emerald-700 font-medium" data-testid={`annual-note-${p.id}`}>billed annually · ${p.annual.toLocaleString()}/yr</p>}
              </div>
              <ul className="mt-4 space-y-2.5 flex-1">
                {p.features.map((f) => (
                  <li key={f} className="flex items-start gap-2 text-sm text-slate-600"><Check className="h-4 w-4 mt-0.5 text-emerald-600 shrink-0" />{f}</li>
                ))}
              </ul>
              <button
                disabled={current || !p.purchasable || busy === p.id}
                onClick={() => (isActive ? openPortal() : checkout(p.id))}
                data-testid={`subscribe-${p.id}`}
                className={`mt-6 rounded-md py-2.5 text-sm font-medium transition-colors ${
                  current ? "bg-slate-100 text-slate-400 cursor-default" : !p.purchasable ? "bg-slate-100 text-slate-400 cursor-default" : "bg-emerald-900 text-white hover:bg-emerald-800"
                }`}
              >
                {current ? "Current Plan" : !p.purchasable ? "Grandfathered" : busy === p.id ? "Redirecting…" : showTrial ? "Start 5-day free trial" : (isActive ? `Switch to ${p.name}` : `Subscribe to ${p.name}`)}
              </button>
            </div>
          );
        })}
      </div>
      {isActive && (
        <div className="rounded-2xl border border-slate-200 bg-white p-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-testid="cancel-section">
          <div>
            <p className="font-heading font-semibold text-slate-900">{sub?.cancel_at_period_end ? "Your plan is set to end" : isTrialing ? "Cancel your free trial" : "Cancel your subscription"}</p>
            <p className="text-sm text-slate-500 mt-1">
              {sub?.cancel_at_period_end
                ? <>Access ends on <span className="font-medium text-slate-700">{fmtDate(sub.current_period_end)}</span>. You can resume anytime before then.</>
                : isTrialing
                ? <>You won't be charged. You keep full access until your trial ends on <span className="font-medium text-slate-700">{fmtDate(sub?.current_period_end)}</span>.</>
                : <>You'll keep access until <span className="font-medium text-slate-700">{fmtDate(sub?.current_period_end)}</span>, then your plan won't renew.</>}
            </p>
          </div>
          {sub?.cancel_at_period_end ? (
            <button onClick={reactivate} disabled={acting} data-testid="resume-plan-button" className="shrink-0 inline-flex items-center justify-center gap-2 rounded-md px-5 py-2.5 text-sm font-medium bg-emerald-900 text-white hover:bg-emerald-800 disabled:opacity-60 transition-colors">
              <RefreshCw className="h-4 w-4" /> Resume plan
            </button>
          ) : (
            <button onClick={confirmCancel} disabled={acting} data-testid="cancel-plan-button" className="shrink-0 inline-flex items-center justify-center gap-2 rounded-md px-5 py-2.5 text-sm font-medium bg-white border border-red-300 text-red-600 hover:bg-red-50 disabled:opacity-60 transition-colors">
              <XCircle className="h-4 w-4" /> Cancel {isTrialing ? "trial" : "subscription"}
            </button>
          )}
        </div>
      )}
      <p className="text-xs text-slate-400">Payments processed securely by Stripe.</p>
    </div>
  );
}
